import * as React from 'react';
import { clsx } from 'clsx';
import * as Popover from '@radix-ui/react-popover';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import {
  format,
  parse,
  isValid,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameDay,
  isToday,
  addMonths,
  subMonths,
  startOfWeek,
  endOfWeek,
} from 'date-fns';

export interface DatePickerProps {
  /**
   * The selected date value
   */
  value?: Date;
  /**
   * Callback when date changes
   */
  onChange?: (date: Date | undefined) => void;
  /**
   * Label for the input
   */
  label?: string;
  /**
   * Placeholder text
   */
  placeholder?: string;
  /**
   * Helper text below the input
   */
  helperText?: string;
  /**
   * Error message
   */
  error?: string;
  /**
   * Success state
   */
  state?: 'default' | 'success' | 'error';
  /**
   * Whether the input is disabled
   */
  disabled?: boolean;
  /**
   * Custom className
   */
  className?: string;
  /**
   * Custom style
   */
  style?: React.CSSProperties;
  /**
   * Test ID for testing
   */
  'data-testid'?: string;
  /**
   * Minimum selectable date
   */
  minDate?: Date;
  /**
   * Maximum selectable date
   */
  maxDate?: Date;
  /**
   * Date format string (default: dd/MM/yyyy)
   */
  dateFormat?: string;
}

const wrapperStyles: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
};

const labelStyles: React.CSSProperties = {
  fontSize: '11px',
  fontWeight: 600,
  color: '#2f2f2f',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
};

const inputContainerStyles: React.CSSProperties = {
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
};

const inputStyles: React.CSSProperties = {
  width: '100%',
  height: '36px',
  padding: '0 36px 0 12px',
  fontSize: '13px',
  border: '1px solid #d1d1d1',
  borderRadius: '8px',
  outline: 'none',
  transition: 'all 0.2s ease-in-out',
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  backgroundColor: '#ffffff',
  color: '#2f2f2f',
};

const iconButtonStyles: React.CSSProperties = {
  position: 'absolute',
  right: '8px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '4px',
  cursor: 'pointer',
  color: '#595959',
  backgroundColor: 'transparent',
  border: 'none',
};

const helperTextStyles: React.CSSProperties = {
  fontSize: '11px',
  color: '#595959',
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
};

const calendarPopoverStyles: React.CSSProperties = {
  backgroundColor: '#ffffff',
  border: '1px solid #efefef',
  borderRadius: '8px',
  padding: '16px',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
  minWidth: '320px',
  zIndex: 1000,
};

const calendarHeaderStyles: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: '16px',
  gap: '8px',
};

const monthYearContainerStyles: React.CSSProperties = {
  display: 'flex',
  gap: '8px',
  flex: 1,
};

const selectStyles: React.CSSProperties = {
  padding: '6px 8px',
  fontSize: '13px',
  border: '1px solid #d1d1d1',
  borderRadius: '6px',
  backgroundColor: '#ffffff',
  color: '#2f2f2f',
  cursor: 'pointer',
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  outline: 'none',
};

const navButtonStyles: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '28px',
  height: '28px',
  border: 'none',
  backgroundColor: 'transparent',
  cursor: 'pointer',
  borderRadius: '4px',
  color: '#2f2f2f',
  transition: 'background-color 0.2s',
};

const weekDaysStyles: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(7, 1fr)',
  gap: '4px',
  marginBottom: '8px',
};

const weekDayStyles: React.CSSProperties = {
  fontSize: '11px',
  fontWeight: 600,
  color: '#7e7e7e',
  textAlign: 'center',
  padding: '4px',
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
};

const daysGridStyles: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(7, 1fr)',
  gap: '4px',
};

const dayButtonStyles: React.CSSProperties = {
  width: '36px',
  height: '36px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: 'none',
  backgroundColor: 'transparent',
  borderRadius: '50%',
  cursor: 'pointer',
  fontSize: '13px',
  color: '#2f2f2f',
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  transition: 'all 0.2s',
};

const footerStyles: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'flex-end',
  marginTop: '12px',
  paddingTop: '12px',
  borderTop: '1px solid #efefef',
};

const todayButtonStyles: React.CSSProperties = {
  padding: '6px 12px',
  fontSize: '13px',
  fontWeight: 500,
  color: '#16a33d',
  backgroundColor: 'transparent',
  border: 'none',
  cursor: 'pointer',
  borderRadius: '6px',
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  transition: 'background-color 0.2s',
};

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const WEEKDAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

/**
 * DatePicker component - Arbor Design System
 *
 * A date picker with calendar popup for selecting dates.
 */
export const DatePicker = React.forwardRef<HTMLInputElement, DatePickerProps>(
  (
    {
      value,
      onChange,
      label,
      placeholder = 'DD/MM/YYYY',
      helperText,
      error,
      state = 'default',
      disabled = false,
      className,
      style,
      'data-testid': dataTestId,
      minDate,
      maxDate,
      dateFormat = 'dd/MM/yyyy',
    },
    ref
  ) => {
    const [open, setOpen] = React.useState(false);
    const [inputValue, setInputValue] = React.useState('');
    const [viewDate, setViewDate] = React.useState(value || new Date());
    const inputId = React.useId();

    // Sync input value with prop value
    React.useEffect(() => {
      if (value && isValid(value)) {
        setInputValue(format(value, dateFormat));
      } else {
        setInputValue('');
      }
    }, [value, dateFormat]);

    // Update view date when value changes
    React.useEffect(() => {
      if (value && isValid(value)) {
        setViewDate(value);
      }
    }, [value]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setInputValue(newValue);

      // Try to parse the input
      const parsedDate = parse(newValue, dateFormat, new Date());
      if (isValid(parsedDate)) {
        onChange?.(parsedDate);
        setViewDate(parsedDate);
      }
    };

    const handleInputBlur = () => {
      // If input is invalid, reset to current value
      if (value && isValid(value)) {
        setInputValue(format(value, dateFormat));
      } else if (!inputValue) {
        onChange?.(undefined);
      }
    };

    const handleDateSelect = (date: Date) => {
      onChange?.(date);
      setOpen(false);
    };

    const handleTodayClick = () => {
      const today = new Date();
      onChange?.(today);
      setViewDate(today);
      setOpen(false);
    };

    const handleMonthChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      const newMonth = parseInt(e.target.value, 10);
      const newDate = new Date(viewDate);
      newDate.setMonth(newMonth);
      setViewDate(newDate);
    };

    const handleYearChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      const newYear = parseInt(e.target.value, 10);
      const newDate = new Date(viewDate);
      newDate.setFullYear(newYear);
      setViewDate(newDate);
    };

    const handlePrevMonth = () => {
      setViewDate(subMonths(viewDate, 1));
    };

    const handleNextMonth = () => {
      setViewDate(addMonths(viewDate, 1));
    };

    // Get days to display in calendar
    const monthStart = startOfMonth(viewDate);
    const monthEnd = endOfMonth(viewDate);
    const startDate = startOfWeek(monthStart, { weekStartsOn: 1 }); // Start week on Monday
    const endDate = endOfWeek(monthEnd, { weekStartsOn: 1 });
    const days = eachDayOfInterval({ start: startDate, end: endDate });

    // Check if date is selectable
    const isDateDisabled = (date: Date) => {
      if (minDate && date < minDate) return true;
      if (maxDate && date > maxDate) return true;
      return false;
    };

    // Generate year options (current year ± 100 years)
    const currentYear = viewDate.getFullYear();
    const yearOptions = Array.from({ length: 201 }, (_, i) => currentYear - 100 + i);

    const currentState = error ? 'error' : state;

    const getInputStyles = (): React.CSSProperties => {
      const styles = { ...inputStyles };

      if (disabled) {
        styles.backgroundColor = '#f8f8f8';
        styles.borderColor = '#efefef';
        styles.color = '#7e7e7e';
        styles.cursor = 'not-allowed';
      } else if (currentState === 'error') {
        styles.borderColor = '#e02f1d';
      } else if (currentState === 'success') {
        styles.borderColor = '#16a33d';
      }

      return styles;
    };

    return (
      <div
        className={clsx('arbor-datepicker-wrapper', className)}
        style={{ ...wrapperStyles, ...style }}
        data-testid={dataTestId}
      >
        {label && (
          <label htmlFor={inputId} style={labelStyles}>
            {label}
          </label>
        )}
        <Popover.Root open={open} onOpenChange={setOpen}>
          <div style={inputContainerStyles}>
            <input
              ref={ref}
              id={inputId}
              type="text"
              value={inputValue}
              onChange={handleInputChange}
              onBlur={handleInputBlur}
              placeholder={placeholder}
              disabled={disabled}
              style={getInputStyles()}
              aria-label={label || 'Date picker'}
            />
            <Popover.Trigger asChild>
              <button
                type="button"
                style={{
                  ...iconButtonStyles,
                  cursor: disabled ? 'not-allowed' : 'pointer',
                }}
                disabled={disabled}
                aria-label="Open calendar"
              >
                <Calendar size={16} />
              </button>
            </Popover.Trigger>
          </div>

          <Popover.Portal>
            <Popover.Content
              align="start"
              sideOffset={4}
              style={calendarPopoverStyles}
            >
              {/* Calendar Header */}
              <div style={calendarHeaderStyles}>
                <button
                  type="button"
                  onClick={handlePrevMonth}
                  style={navButtonStyles}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#f8f8f8';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                  aria-label="Previous month"
                >
                  <ChevronLeft size={16} />
                </button>

                <div style={monthYearContainerStyles}>
                  <select
                    value={viewDate.getMonth()}
                    onChange={handleMonthChange}
                    style={selectStyles}
                    aria-label="Select month"
                  >
                    {MONTHS.map((month, index) => (
                      <option key={month} value={index}>
                        {month}
                      </option>
                    ))}
                  </select>

                  <select
                    value={viewDate.getFullYear()}
                    onChange={handleYearChange}
                    style={selectStyles}
                    aria-label="Select year"
                  >
                    {yearOptions.map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                </div>

                <button
                  type="button"
                  onClick={handleNextMonth}
                  style={navButtonStyles}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#f8f8f8';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                  aria-label="Next month"
                >
                  <ChevronRight size={16} />
                </button>
              </div>

              {/* Weekday Headers */}
              <div style={weekDaysStyles}>
                {WEEKDAYS.map((day) => (
                  <div key={day} style={weekDayStyles}>
                    {day}
                  </div>
                ))}
              </div>

              {/* Days Grid */}
              <div style={daysGridStyles}>
                {days.map((day) => {
                  const isCurrentMonth = day.getMonth() === viewDate.getMonth();
                  const isSelected = value && isSameDay(day, value);
                  const isTodayDate = isToday(day);
                  const isDayDisabled = isDateDisabled(day);

                  const getDayButtonStyles = (): React.CSSProperties => {
                    const styles = { ...dayButtonStyles };

                    if (!isCurrentMonth) {
                      styles.color = '#d1d1d1';
                    }

                    if (isDayDisabled) {
                      styles.color = '#d1d1d1';
                      styles.cursor = 'not-allowed';
                    }

                    if (isSelected) {
                      styles.backgroundColor = '#3cad51';
                      styles.color = '#ffffff';
                    } else if (isTodayDate && !isDayDisabled) {
                      styles.fontWeight = 600;
                      styles.color = '#16a33d';
                    }

                    return styles;
                  };

                  return (
                    <button
                      key={day.toISOString()}
                      type="button"
                      onClick={() => !isDayDisabled && handleDateSelect(day)}
                      disabled={isDayDisabled}
                      style={getDayButtonStyles()}
                      onMouseEnter={(e) => {
                        if (!isSelected && !isDayDisabled && isCurrentMonth) {
                          e.currentTarget.style.backgroundColor = '#f8f8f8';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isSelected) {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }
                      }}
                      aria-label={format(day, 'MMMM d, yyyy')}
                      aria-selected={isSelected}
                    >
                      {format(day, 'd')}
                    </button>
                  );
                })}
              </div>

              {/* Footer with Today button */}
              <div style={footerStyles}>
                <button
                  type="button"
                  onClick={handleTodayClick}
                  style={todayButtonStyles}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#f8f8f8';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  Today
                </button>
              </div>
            </Popover.Content>
          </Popover.Portal>
        </Popover.Root>

        {error && (
          <span style={{ ...helperTextStyles, color: '#e02f1d' }}>{error}</span>
        )}
        {!error && helperText && (
          <span style={helperTextStyles}>{helperText}</span>
        )}
      </div>
    );
  }
);

DatePicker.displayName = 'DatePicker';
